import { type RefObject, useEffect, useState } from 'react';

/**
 * Detects camera stream interruption via MediaStreamTrack ended/mute events.
 */
export function useCameraInterrupted(
  videoRef: RefObject<HTMLVideoElement | null>,
  enabled: boolean,
): boolean {
  const [interrupted, setInterrupted] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setInterrupted(false);
      return;
    }

    const video = videoRef.current;
    const stream = video?.srcObject;
    if (!(stream instanceof MediaStream)) {
      return;
    }

    const tracks = stream.getVideoTracks();
    if (tracks.length === 0) {
      setInterrupted(true);
      return;
    }

    const onEnded = () => setInterrupted(true);

    for (const track of tracks) {
      if (track.readyState === 'ended') {
        setInterrupted(true);
      }
      track.addEventListener('ended', onEnded);
    }

    return () => {
      for (const track of tracks) {
        track.removeEventListener('ended', onEnded);
      }
    };
  }, [enabled, videoRef]);

  return interrupted;
}
